import { useEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";

import { Input, Textarea } from "../../../components/ui";
import {
  fetchBoletaPreview,
  type BoletaPreviewEscenario,
  type EmpresaConfiguracion,
  type EmpresaConfiguracionUpdate,
} from "../api";
import { SaveBar, nullIfEmpty, useConfigForm } from "./_shared";

interface Form {
  mensaje_boleta: string;
  texto_resolucion_sii: string;
  monto_multa_inasistencia: string;
  tasa_interes_mora_anual_pct: string;
  dias_vencimiento_boleta: string;
  cargo_corte: string;
  cargo_reposicion: string;
  cuota_incorporacion: string;
  cargo_conexion: string;
  aviso_corte_en_boleta: boolean;
  aviso_corte_min_boletas: string;
  aviso_corte_texto: string;
}

const extractor = (cfg: EmpresaConfiguracion): Form => ({
  mensaje_boleta: cfg.mensaje_boleta ?? "",
  texto_resolucion_sii: cfg.texto_resolucion_sii ?? "",
  monto_multa_inasistencia:
    cfg.monto_multa_inasistencia == null
      ? ""
      : String(cfg.monto_multa_inasistencia),
  tasa_interes_mora_anual_pct: String(cfg.tasa_interes_mora_anual_pct),
  dias_vencimiento_boleta: String(cfg.dias_vencimiento_boleta),
  cargo_corte: String(cfg.cargo_corte),
  cargo_reposicion: String(cfg.cargo_reposicion),
  cuota_incorporacion: String(cfg.cuota_incorporacion),
  cargo_conexion: String(cfg.cargo_conexion),
  aviso_corte_en_boleta: cfg.aviso_corte_en_boleta,
  aviso_corte_min_boletas: String(cfg.aviso_corte_min_boletas),
  aviso_corte_texto: cfg.aviso_corte_texto ?? "",
});

const parseInt0 = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
};

const parseFloat0 = (s: string): number => {
  const n = Number.parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const differ = (
  f: Form,
  cfg: EmpresaConfiguracion,
): EmpresaConfiguracionUpdate => {
  const out: EmpresaConfiguracionUpdate = {};
  if (nullIfEmpty(f.mensaje_boleta) !== cfg.mensaje_boleta)
    out.mensaje_boleta = nullIfEmpty(f.mensaje_boleta);
  if (nullIfEmpty(f.texto_resolucion_sii) !== cfg.texto_resolucion_sii)
    out.texto_resolucion_sii = nullIfEmpty(f.texto_resolucion_sii);

  const multaNueva = parseInt0(f.monto_multa_inasistencia);
  if (multaNueva !== cfg.monto_multa_inasistencia)
    out.monto_multa_inasistencia = multaNueva;

  const tasaNueva = parseFloat0(f.tasa_interes_mora_anual_pct);
  if (tasaNueva !== cfg.tasa_interes_mora_anual_pct)
    out.tasa_interes_mora_anual_pct = tasaNueva;

  const diasNuevo = Math.min(90, Math.max(1, parseInt0(f.dias_vencimiento_boleta) ?? 20));
  if (diasNuevo !== cfg.dias_vencimiento_boleta)
    out.dias_vencimiento_boleta = diasNuevo;

  const corteNuevo = Math.max(0, parseInt0(f.cargo_corte) ?? 0);
  if (corteNuevo !== cfg.cargo_corte) out.cargo_corte = corteNuevo;
  const reposicionNueva = Math.max(0, parseInt0(f.cargo_reposicion) ?? 0);
  if (reposicionNueva !== cfg.cargo_reposicion) out.cargo_reposicion = reposicionNueva;
  const cuotaNueva = Math.max(0, parseInt0(f.cuota_incorporacion) ?? 0);
  if (cuotaNueva !== cfg.cuota_incorporacion) out.cuota_incorporacion = cuotaNueva;
  const conexionNueva = Math.max(0, parseInt0(f.cargo_conexion) ?? 0);
  if (conexionNueva !== cfg.cargo_conexion) out.cargo_conexion = conexionNueva;

  if (f.aviso_corte_en_boleta !== cfg.aviso_corte_en_boleta)
    out.aviso_corte_en_boleta = f.aviso_corte_en_boleta;

  const minNuevo = Math.max(1, parseInt0(f.aviso_corte_min_boletas) ?? 1);
  if (minNuevo !== cfg.aviso_corte_min_boletas)
    out.aviso_corte_min_boletas = minNuevo;

  if (nullIfEmpty(f.aviso_corte_texto) !== cfg.aviso_corte_texto)
    out.aviso_corte_texto = nullIfEmpty(f.aviso_corte_texto);

  return out;
};

const ESCENARIOS: { id: BoletaPreviewEscenario; label: string }[] = [
  { id: "normal", label: "Boleta normal" },
  { id: "con_subsidio", label: "Con subsidio FNDR" },
  { id: "con_saldo", label: "Con saldo anterior" },
  { id: "con_corte", label: "Con corte en trámite" },
  { id: "completo", label: "Completo (todos los conceptos)" },
];

export function BoletaTab({
  cfg,
}: {
  cfg: EmpresaConfiguracion;
}): JSX.Element {
  const { form, setForm, haycambios, pending, reset, save } = useConfigForm(
    cfg,
    extractor,
    differ,
  );

  // ── Preview ──────────────────────────────────────────────────────────
  const [escenario, setEscenario] = useState<BoletaPreviewEscenario>("normal");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Debounce 500ms para no spamear el backend mientras el operador tipea.
    const handle = window.setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setPreviewLoading(true);
      setPreviewError(null);
      fetchBoletaPreview(
        {
          escenario,
          mensaje_boleta: nullIfEmpty(form.mensaje_boleta),
          texto_resolucion_sii: nullIfEmpty(form.texto_resolucion_sii),
          aviso_corte: form.aviso_corte_en_boleta
            ? (nullIfEmpty(form.aviso_corte_texto) ??
                "Usted registra {n} boleta(s) vencida(s). Si no regulariza su deuda, el servicio podrá ser suspendido conforme a la Ley N° 20.998.").replace(
                "{n}",
                "2",
              )
            : null,
        },
        ac.signal,
      )
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
        })
        .catch((err) => {
          if (err?.name === "AbortError") return;
          setPreviewError(
            err instanceof Error ? err.message : "No se pudo generar preview",
          );
        })
        .finally(() => setPreviewLoading(false));
    }, 500);

    return () => window.clearTimeout(handle);
  }, [
    escenario,
    form.mensaje_boleta,
    form.texto_resolucion_sii,
    form.aviso_corte_en_boleta,
    form.aviso_corte_texto,
  ]);

  // Cleanup al desmontar.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
      {/* ── FORM ────────────────────────────────────────────────────── */}
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          Textos y reglas que aplican a la emisión de boletas y a las multas
          operativas del comité. Los datos del banco se editan en el tab
          <strong> Banco</strong> y el correo de reclamos en el tab
          <strong> Atención al usuario</strong>.
        </p>

        <div>
          <Textarea
            label="Mensaje al pie de boleta"
            hint="aparece en cada boleta del comité"
            rows={3}
            value={form.mensaje_boleta}
            onChange={(e) =>
              setForm({ ...form, mensaje_boleta: e.target.value })
            }
            placeholder="Recuerde pagar antes del día 25. Para reclamos: reclamos@comite.cl"
            maxLength={500}
          />
          <div className="mt-1 text-xs text-neutral-600 text-right">
            {form.mensaje_boleta.length}/500
          </div>
        </div>

        <Input
          label="Texto de resolución SII"
          hint="Ej: 'Res. Ex. SII Nº 80, 22-08-2014'"
          type="text"
          value={form.texto_resolucion_sii}
          onChange={(e) =>
            setForm({ ...form, texto_resolucion_sii: e.target.value })
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Monto multa por inasistencia"
            hint="CLP, aplica a asambleas"
            type="number"
            min={0}
            step={500}
            value={form.monto_multa_inasistencia}
            onChange={(e) =>
              setForm({ ...form, monto_multa_inasistencia: e.target.value })
            }
            placeholder="5000"
          />
          <Input
            label="Tasa interés mora anual (%)"
            hint="0 = no aplica intereses"
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={form.tasa_interes_mora_anual_pct}
            onChange={(e) =>
              setForm({
                ...form,
                tasa_interes_mora_anual_pct: e.target.value,
              })
            }
          />
          <Input
            label="Días para el vencimiento"
            hint="Desde la fecha de emisión de la boleta (1–90)"
            type="number"
            min={1}
            max={90}
            step={1}
            value={form.dias_vencimiento_boleta}
            onChange={(e) =>
              setForm({ ...form, dias_vencimiento_boleta: e.target.value })
            }
          />
          <Input
            label="Cargo por corte (CLP)"
            hint="Se cobra en la boleta siguiente al corte. 0 = no se cobra"
            type="number"
            min={0}
            step={1}
            value={form.cargo_corte}
            onChange={(e) => setForm({ ...form, cargo_corte: e.target.value })}
          />
          <Input
            label="Cargo por reposición (CLP)"
            hint="Se cobra en la boleta siguiente a la reposición"
            type="number"
            min={0}
            step={1}
            value={form.cargo_reposicion}
            onChange={(e) => setForm({ ...form, cargo_reposicion: e.target.value })}
          />
          <Input
            label="Cuota de incorporación (CLP)"
            hint="La paga quien se hace socio. Se cobra en su boleta siguiente, exenta de IVA. 0 = no se cobra"
            type="number"
            min={0}
            step={1}
            value={form.cuota_incorporacion}
            onChange={(e) =>
              setForm({ ...form, cuota_incorporacion: e.target.value })
            }
          />
          <Input
            label="Paquete de conexión nueva (CLP)"
            hint="Instalación, medidor y materiales en un solo monto. Se cobra en la boleta siguiente, exento de IVA. 0 = no se cobra"
            type="number"
            min={0}
            step={1}
            value={form.cargo_conexion}
            onChange={(e) => setForm({ ...form, cargo_conexion: e.target.value })}
          />
        </div>

        {/* ── Aviso de corte en la boleta ─────────────────────────── */}
        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-neutral-450"
              checked={form.aviso_corte_en_boleta}
              onChange={(e) =>
                setForm({ ...form, aviso_corte_en_boleta: e.target.checked })
              }
            />
            <span className="text-sm">
              <span className="font-medium text-neutral-800">
                Mostrar aviso de corte por mora en la boleta
              </span>
              <span className="block text-xs text-neutral-600">
                Si el socio tiene boletas vencidas al emitir, la boleta incluye
                un recuadro rojo de aviso (Ley 20.998).
              </span>
            </span>
          </label>

          {form.aviso_corte_en_boleta && (
            <div className="space-y-3 pl-6">
              <Input
                label="Umbral de boletas vencidas"
                hint="cuántas boletas vencidas disparan el aviso"
                type="number"
                min={1}
                max={99}
                value={form.aviso_corte_min_boletas}
                onChange={(e) =>
                  setForm({ ...form, aviso_corte_min_boletas: e.target.value })
                }
                placeholder="1"
              />
              <div>
                <Textarea
                  label="Texto del aviso (opcional)"
                  hint="usa {n} para la cantidad de boletas vencidas; vacío = texto por defecto"
                  rows={3}
                  value={form.aviso_corte_texto}
                  onChange={(e) =>
                    setForm({ ...form, aviso_corte_texto: e.target.value })
                  }
                  placeholder="Usted registra {n} boleta(s) vencida(s). Regularice para evitar el corte del servicio."
                  maxLength={500}
                />
                <div className="mt-1 text-xs text-neutral-600 text-right">
                  {form.aviso_corte_texto.length}/500
                </div>
              </div>
            </div>
          )}
        </div>

        <SaveBar
          haycambios={haycambios}
          pending={pending}
          reset={reset}
          save={save}
        />
      </div>

      {/* ── PREVIEW ─────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
            <Eye className="h-4 w-4" strokeWidth={1.5} />
            Vista previa de la boleta
            {previewLoading && (
              <span className="text-xs font-normal text-neutral-600">
                generando…
              </span>
            )}
          </div>
          <select
            value={escenario}
            onChange={(e) =>
              setEscenario(e.target.value as BoletaPreviewEscenario)
            }
            className="rounded-md border border-neutral-450 bg-white px-2 py-1 text-xs shadow-sm"
          >
            {ESCENARIOS.map((esc) => (
              <option key={esc.id} value={esc.id}>
                {esc.label}
              </option>
            ))}
          </select>
        </div>

        <p className="text-xs text-neutral-600">
          Datos del socio, consumo y montos son ficticios. Los datos del banco
          y reclamos vienen de los otros tabs ya guardados.
        </p>

        <div className="rounded-md ring-1 ring-neutral-200 bg-neutral-50 overflow-hidden">
          {previewError ? (
            <div className="p-4 text-sm text-rose-700">{previewError}</div>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              title="Boleta preview"
              className="w-full"
              style={{ minHeight: "780px", border: 0 }}
            />
          ) : (
            <div className="p-4 text-sm text-neutral-600">
              {previewLoading ? "Generando vista previa…" : "—"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
